<template>
  <div
    v-if="isAppReady"
    id="app"
    :class="{'is-animating': isAnimating}"
    @click="resetAppTimeout"
  >
    <div class="overlay"></div>
    <transition name="fade">
      <router-view class="router-view"/>
    </transition>
    <transition name="fade">
      <right-sidebar
        v-if="isBottomBarVisible"
        :logo="$store.state.config.sidebarLogo"
        :minimap="sidebarMinimap"
        :heading="sidebarHeading"
        :description="sidebarDescription"
        :qr-url="qrUrl"
      />
    </transition>
    <transition name="slide-in-up">
      <sub-bottom-bar v-if="activeSubNavigation && activeSubNavigation.length"></sub-bottom-bar>
    </transition>
    <transition name="fade">
      <bottom-bar v-if="isBottomBarVisible"></bottom-bar>
    </transition>
  </div>
</template>
<script>
import EventBus from '@/store/event-bus'
import BottomBar from '@/components/BottomBar'
import RightSidebar from '@/components/RightSidebar'
import SubBottomBar from '@/components/SubBottomBar'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'

// No change pls
const BASE_VIEWPORT_WIDTH = 1920
const BASE_FONT_SIZE = 10

export default {
  components: { BottomBar, RightSidebar, SubBottomBar },

  data () {
    return {
      appTimeout: null,
      sidebarMinimap: "",
      sidebarHeading: "",
      sidebarDescription: "",
      qrUrl: "",
    }
  },

  computed: {
    ...mapGetters([
      'activePage',
      'activeSubNavigation',
      'isAnimating',
      'language',
    ]),

    isAppReady () {
      return this.$store.state.config && this.$store.state.pages && this.$store.state.navigation
    },

    isBottomBarVisible () {
      return this.$store.state.isBottomBarVisible
    }
  },

  watch: {
    activePage () {
      if (this.activePage) {
        this.$store.commit('INTERACTIVE_SLIDES_ANIMATE_END')
        this.$store.commit('TAGS_ANIMATE_END')
        this.$nextTick(() => {
          this.sidebarMinimap = this.activePage.minimapImage ? this.$store.state.assetsPath + this.activePage.minimapImage : ''
          this.sidebarHeading = ''
          this.sidebarDescription = ''
          this.qrUrl = ''

          if (!this.activeSubNavigation.length && !this.activePage.interactiveSlides.length && !this.activePage.tags.length && !this.isAnimating) {
            console.log('activePage setSidebar')
            this.setSidebarData()
          }
        })
      }
    },

    $route (e) {
      this.$store.dispatch('storeVisitorInfo')
    }
  },

  methods: {
    ...mapActions([
      'fetchData'
    ]),

    initAnimationListeners () {
      EventBus.$on('MAIN_NAV_ANIMATE_END', () => {
        if (!this.activeSubNavigation.length && !this.activePage.interactiveSlides.length && !this.activePage.tags.length) {
          this.setSidebarData()
        }
      })
      EventBus.$on('SUB_NAV_ANIMATE_END', () => {
        if (!this.activePage.interactiveSlides.length && !this.activePage.tags.length) {
          this.setSidebarData()
        }
      })
      EventBus.$on('INTERACTIVE_SLIDES_ANIMATE_END', () => {
        if (!this.activePage.tags.length) {
          this.setSidebarData()
        }
      })
      EventBus.$on('TAGS_ANIMATE_END', () => {
        this.setSidebarData()
      })
    },

    setSidebarData () {
      this.sidebarHeading = this.activePage.heading[this.language.code]
      this.sidebarDescription = this.activePage.description[this.language.code]
      this.qrUrl = this.activePage.qrUrl
    },

    // We use cookies to set some variables on a per browser level
    // For example - one touchscreen might need to trigger video wall videos
    // This touchscreen will load http://localhost?triggerVideos=true which will set the required variable
    // This is then set as a session cookie so even refreshing to http://localhost/#/ wont lose the settings
    handleCookies () {
      if (typeof this.$route.query.triggerVideos !== 'undefined') {
        document.cookie = `triggerVideos=${this.$route.query.triggerVideos}`
      }

      if (typeof this.$route.query.hideLiveHere !== 'undefined') {
        document.cookie = `hideLiveHere=${this.$route.query.hideLiveHere}`
      }

      const triggerVideos = document.cookie.replace(/(?:(?:^|.*;\s*)triggerVideos\s*=\s*([^;]*).*$)|^.*$/, '$1') == 'true' ? true : false
      const hideLiveHere = document.cookie.replace(/(?:(?:^|.*;\s*)hideLiveHere\s*=\s*([^;]*).*$)|^.*$/, '$1') == 'true' ? true : false

      if (typeof triggerVideos !== 'undefined') {
        this.$store.state.triggerVideos = triggerVideos
      }

      if (typeof hideLiveHere !== 'undefined') {
        this.$store.state.hideLiveHere = hideLiveHere
      }
    },

    resetAppTimeout () {
      if (this.appTimeout) {
        clearInterval(this.appTimeout)
      }

      this.appTimeout = setInterval(() => {
        if (process.env.VUE_APP_ENV === 'production') {
          window.location.href = window.location.origin
        }
      }, 1000 * 600)
    },

    // Super important: The base font size is used to position and size everything using REM at different screen sizes
    setBaseFontSize () {
      this.$store.state.appScale = window.innerWidth / BASE_VIEWPORT_WIDTH
      document.documentElement.style.fontSize = `${this.$store.state.appScale * BASE_FONT_SIZE}px`
    },
  },

  created () {
    this.fetchData()
  },

  mounted () {
    this.handleCookies()
    this.resetAppTimeout()

    // Debounce would probably be more performant but 🤷, throttle feels nicer
    window.addEventListener('resize', _.throttle(() => {
      this.setBaseFontSize()
    }, 100))
    this.setBaseFontSize()

    this.initAnimationListeners()
  }
}
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/_animations.scss";
@import "@/styles/_type.scss";

html {
  background-color: #333;
  color: #fff;
  font-family: $touchscreen-font;
  font-size: 62.5%;
  font-weight: 400;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}

body {
  margin: 0;
}

#app {
  background-color: #333;
  height: 108rem;
  overflow: hidden;
  position: relative;
  width: 192rem;
}

* {
  box-sizing: border-box;
  outline: none;
  pointer-events: none;
  user-select: none;
  -webkit-font-smoothing: subpixel-antialiased;
}

.touch {
  pointer-events: auto;

  .is-animating & {
    // pointer-events: none;
  }
}

a {
  color: $white;
  text-decoration: none;
}

p {
  margin-bottom: .6em;
  margin-top: 0;
}

button {
  border: none;
  background-color: transparent;
  color: $white;
  font-family: $touchscreen-font;
  letter-spacing: 0.05em;
}

.main {
  height: 98rem;
  left: 0;
  position: absolute;
  top: 0;
  width: 159.5rem;
}

.overlay {
  height: 100%;
  opacity: .5;
  pointer-events: none;
  position: relative;
  z-index: 1000;

  &::before {
    background: linear-gradient(90deg,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.738) 19%,
      rgba(0, 0, 0, 0.541) 34%,
      rgba(0, 0, 0, 0.382) 47%,
      rgba(0, 0, 0, 0.278) 56.5%,
      rgba(0, 0, 0, 0.194) 65%,
      rgba(0, 0, 0, 0.126) 73%,
      rgba(0, 0, 0, 0.075) 80.2%,
      rgba(0, 0, 0, 0.042) 86.1%,
      rgba(0, 0, 0, 0.021) 91%,
      rgba(0, 0, 0, 0.008) 95.2%,
      rgba(0, 0, 0, 0.002) 98.2%,
      rgba(0, 0, 0, 0) 100%
    );
    bottom: 0;
    content: '';
    left: 0;
    position: absolute;
    top: 0;
    width: $overlay-gradient-width;
  }

  &::after {
    background: linear-gradient(270deg,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.738) 19%,
      rgba(0, 0, 0, 0.541) 34%,
      rgba(0, 0, 0, 0.382) 47%,
      rgba(0, 0, 0, 0.278) 56.5%,
      rgba(0, 0, 0, 0.194) 65%,
      rgba(0, 0, 0, 0.126) 73%,
      rgba(0, 0, 0, 0.075) 80.2%,
      rgba(0, 0, 0, 0.042) 86.1%,
      rgba(0, 0, 0, 0.021) 91%,
      rgba(0, 0, 0, 0.008) 95.2%,
      rgba(0, 0, 0, 0.002) 98.2%,
      rgba(0, 0, 0, 0) 100%
    );
    bottom: 0;
    content: '';
    right: 0;
    position: absolute;
    top: 0;
    width: $overlay-gradient-width;
  }
}

.tag-rich__description svg {
  margin-top: 1em;
  height: 5rem;
  width: 5rem;
}
</style>
